/**
 * 管理者用: SA 請求書 一覧 (v3.2.14 / Phase 3a)。
 *
 * URL: /sales/sa-invoices
 *
 * 全 SA からの請求書を一覧表示。経理担当 (or owner) が:
 *  - 「支払済マーク」ボタン → 請求書 + 含まれる records 全部 paid に一括遷移
 *  - 「取消」ボタン → 請求書 cancelled + records pending に戻る (sent のみ)
 *  - 公開URLコピー / 詳細閲覧
 */
import type { CommissionInvoice, CommissionInvoiceStatus } from '@goenhub/shared';
import { COMMISSION_INVOICE_STATUS_LABELS } from '@goenhub/shared';
import { CheckCircle2, ClipboardCopy, Coins, FileText, Loader2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  invoiceLineBreakdown,
  useCancelCommissionInvoice,
  useCommissionInvoices,
  useMarkCommissionInvoicePaid,
} from '@/hooks/useCommissionInvoices';

function breakdownLabel(inv: CommissionInvoice): string {
  const { salesCount, briefingCount } = invoiceLineBreakdown(inv);
  const parts: string[] = [];
  if (salesCount > 0) parts.push(`報酬 ${salesCount} 件`);
  if (briefingCount > 0) parts.push(`個別説明 ${briefingCount} 件`);
  return parts.join(' ・ ') || '0 件';
}

const STATUS_FILTERS: Array<{ value: CommissionInvoiceStatus | 'all'; label: string }> = [
  { value: 'all', label: 'すべて' },
  { value: 'sent', label: '請求済 (未払い)' },
  { value: 'paid', label: '支払済' },
  { value: 'cancelled', label: '取消' },
];

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function statusBadgeVariant(s: CommissionInvoice['status']) {
  if (s === 'paid') return 'approved' as const;
  if (s === 'sent') return 'review' as const;
  if (s === 'cancelled') return 'danger' as const;
  return 'default' as const;
}

export function CommissionInvoicesPage() {
  const invoices = useCommissionInvoices();
  const markPaid = useMarkCommissionInvoicePaid();
  const cancel = useCancelCommissionInvoice();
  const [filter, setFilter] = useState<CommissionInvoiceStatus | 'all'>('sent');

  const filtered = useMemo(() => {
    if (filter === 'all') return invoices.data ?? [];
    return (invoices.data ?? []).filter((i) => i.status === filter);
  }, [invoices.data, filter]);

  const summary = useMemo(() => {
    const acc = { sent: 0, paid: 0 };
    for (const i of invoices.data ?? []) {
      if (i.status === 'sent') acc.sent += i.totalIncludingTax;
      if (i.status === 'paid') acc.paid += i.totalIncludingTax;
    }
    return acc;
  }, [invoices.data]);

  const handleMarkPaid = async (inv: CommissionInvoice & { id: string }) => {
    const msg = `請求書「${inv.invoiceNumber}」(${breakdownLabel(inv)}) を支払済としてマークします。\n\n含まれる報酬レコードも一括で「支払済」になり、SA「${inv.recipientName}」宛に振込完了通知メールが送信されます。\n\nよろしいですか？`;
    if (!confirm(msg)) return;
    try {
      const res = await markPaid.mutateAsync({ invoiceId: inv.id });
      toast.success(
        `支払済にしました (${res.recordCount} 件)${res.mailSent ? ' + 通知メール送信完了' : ''}`,
      );
      if (!res.mailSent) {
        toast.warning(`メール送信失敗: ${res.mailReason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleCancel = async (inv: CommissionInvoice & { id: string }) => {
    if (
      !confirm(
        `請求書「${inv.invoiceNumber}」(${breakdownLabel(inv)}) を取り消します。\n含まれる報酬レコードは「未申請」に、個別説明代行は「未請求」に戻ります。よろしいですか？`,
      )
    )
      return;
    try {
      await cancel.mutateAsync({ invoiceId: inv.id });
      toast.success('請求書を取り消しました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('公開URLをコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  return (
    <Screen
      title="SA 請求書"
      description="SA から提出された請求書の一覧。「支払済マーク」で含まれる報酬レコードも一括完了します。"
    >
      <div className="space-y-4">
        {/* サマリ */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
            <div className="text-[10px] text-[var(--text-muted)]">未払い (請求済)</div>
            <div className="text-lg font-semibold tabular-nums text-[var(--accent-amber,_#d4a373)]">
              ¥{summary.sent.toLocaleString()}
            </div>
          </div>
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
            <div className="text-[10px] text-[var(--text-muted)]">支払済</div>
            <div className="text-lg font-semibold tabular-nums text-[var(--accent-emerald,_#10b981)]">
              ¥{summary.paid.toLocaleString()}
            </div>
          </div>
        </div>

        {/* フィルタ */}
        <div className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-2">
          <span className="text-[10px] text-[var(--text-muted)] mr-2">ステータス:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                filter === f.value
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                  : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* 一覧 */}
        {invoices.isLoading ? (
          <Spinner />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Coins}
            title="該当する請求書がありません"
            description="SA が /sa/deals の「請求可能」タブから請求書を発行するとここに表示されます。"
          />
        ) : (
          <div className="rounded-md border border-[var(--border)] overflow-hidden">
            <div className="grid grid-cols-[140px_1.5fr_1fr_90px_80px_120px_180px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
              <div>請求番号</div>
              <div>SA / 期間</div>
              <div>件数 / 振込先</div>
              <div>発行日</div>
              <div className="text-center">状態</div>
              <div className="text-right">合計</div>
              <div className="text-center">操作</div>
            </div>
            {filtered.map((inv) => {
              const publicUrl = `${window.location.origin}/c/sa-invoice/${inv.publicToken}`;
              return (
                <div
                  key={inv.id}
                  className="grid grid-cols-[140px_1.5fr_1fr_90px_80px_120px_180px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)] hover:bg-[var(--bg-hover)]"
                >
                  <div className="font-mono tabular-nums">{inv.invoiceNumber}</div>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{inv.recipientName}</div>
                    <div className="truncate text-[10px] text-[var(--text-muted)]">
                      {inv.periodLabel ?? '(期間ラベルなし)'}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate">{breakdownLabel(inv)}</div>
                    {inv.bankAccountSnapshot && (
                      <div className="truncate text-[10px] text-[var(--text-muted)]">
                        {inv.bankAccountSnapshot.bankName} {inv.bankAccountSnapshot.accountNumber}
                      </div>
                    )}
                  </div>
                  <div className="text-[var(--text-muted)] tabular-nums">
                    {fmtDate(inv.issuedAt)}
                  </div>
                  <div className="text-center">
                    <Badge variant={statusBadgeVariant(inv.status)}>
                      {COMMISSION_INVOICE_STATUS_LABELS[inv.status]}
                    </Badge>
                  </div>
                  <div className="text-right tabular-nums font-semibold">
                    ¥{inv.totalIncludingTax.toLocaleString()}
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(publicUrl)}
                      title="公開URLコピー"
                      className="size-7 inline-flex items-center justify-center rounded-sm hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
                    >
                      <ClipboardCopy className="size-3.5" />
                    </button>
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="請求書を開く"
                      className="size-7 inline-flex items-center justify-center rounded-sm hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
                    >
                      <FileText className="size-3.5" />
                    </a>
                    {inv.status === 'sent' && (
                      <>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => handleMarkPaid(inv)}
                          disabled={markPaid.isPending}
                          title="支払済マーク (一括完了)"
                        >
                          {markPaid.isPending ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <CheckCircle2 className="size-3" />
                          )}
                          支払済
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleCancel(inv)}
                          disabled={cancel.isPending}
                          title="取消 (records は未申請に戻る)"
                        >
                          {cancel.isPending ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <XCircle className="size-3" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Screen>
  );
}
